import React, { useState, useEffect } from 'react';
import { captureFromCamera, shouldUseMediaStreamCamera } from '../../utils/cameraUtils';
import CameraModal from './CameraModal';
import './FloatingActionButton.css';

function FloatingActionButton({ 
  onCapture, 
  disabled = false, 
  visible = true,
  uploadProgress = 0 
}) {
  const [state, setState] = useState('idle'); // idle | capturing | uploading | success | error
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [useMediaStream, setUseMediaStream] = useState(false);

  // Check if we should use MediaStream camera
  useEffect(() => {
    const shouldUse = shouldUseMediaStreamCamera();
    setUseMediaStream(shouldUse);
    console.log('[FAB] Use MediaStream camera:', shouldUse);
  }, []);

  // Auto-reset state after success/error
  useEffect(() => {
    if (state === 'success' || state === 'error') {
      const timer = setTimeout(() => {
        setState('idle');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [state]);

  // Update state based on upload progress
  useEffect(() => {
    if (uploadProgress > 0 && uploadProgress < 100) {
      setState('uploading');
    } else if (uploadProgress === 100) {
      setState('success');
    }
  }, [uploadProgress]);

  const handleClick = async () => {
    if (disabled || state !== 'idle') return;

    try {
      setState('capturing');
      
      // Use MediaStream camera if supported, otherwise fall back to file input
      if (useMediaStream) {
        // Open camera modal
        console.log('[FAB] Opening MediaStream camera modal');
        setShowCameraModal(true);
      } else {
        // Fall back to file input method
        console.log('[FAB] Using file input camera (fallback)');
        const photo = await captureFromCamera();
        
        if (photo && onCapture) {
          await onCapture(photo);
        }
      }
    } catch (error) {
      console.error('[FAB] Capture failed:', error);
      if (error.message !== 'Camera capture cancelled') {
        setState('error');
      } else {
        setState('idle');
      }
    }
  };

  // Handle photo capture from MediaStream camera
  const handleCameraCapture = async (photo) => {
    try {
      console.log('[FAB] Photo captured from camera modal');
      
      if (photo && onCapture) {
        await onCapture(photo);
      }
      
      setState('idle');
    } catch (error) {
      console.error('[FAB] Upload failed:', error);
      setState('error');
    }
  };

  // Handle camera modal close
  const handleCameraClose = () => {
    console.log('[FAB] Camera modal closed');
    setShowCameraModal(false);
    setState('idle');
  };

  const handleTouchStart = (e) => {
    if (disabled || state !== 'idle') return;
    
    const timer = setTimeout(() => {
      // Long press detected
      setShowQuickActions(true);
      // Haptic feedback if available
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
    }, 500);
    
    setLongPressTimer(timer);
  };

  const handleTouchEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  const getIcon = () => {
    switch (state) {
      case 'capturing':
        return '📸';
      case 'uploading':
        return '⏳';
      case 'success':
        return '✓';
      case 'error':
        return '✗';
      default:
        return '📷';
    }
  };

  const getClassName = () => {
    let className = 'fab';
    if (!visible) className += ' hidden';
    if (disabled) className += ' disabled';
    className += ` fab-${state}`;
    return className;
  };

  if (!visible) return null;

  return (
    <>
      <button
        className={getClassName()}
        onClick={handleClick}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        disabled={disabled || state !== 'idle'}
        aria-label="Take photo"
      >
        <span className="fab-icon">{getIcon()}</span>
        
        {/* Upload Progress Ring */}
        {state === 'uploading' && uploadProgress > 0 && (
          <svg className="fab-progress" viewBox="0 0 60 60">
            <circle
              className="fab-progress-bg"
              cx="30"
              cy="30"
              r="26"
              fill="none"
              stroke="rgba(255,255,255,0.2)"
              strokeWidth="3"
            />
            <circle
              className="fab-progress-bar"
              cx="30"
              cy="30"
              r="26"
              fill="none"
              stroke="#fff"
              strokeWidth="3"
              strokeDasharray={`${2 * Math.PI * 26}`}
              strokeDashoffset={`${2 * Math.PI * 26 * (1 - uploadProgress / 100)}`}
              transform="rotate(-90 30 30)"
            />
          </svg>
        )}
      </button>

      {/* Camera Modal (MediaStream) */}
      {useMediaStream && (
        <CameraModal
          isOpen={showCameraModal}
          onCapture={handleCameraCapture}
          onClose={handleCameraClose}
          initialFacingMode="environment"
        />
      )}

      {/* Quick Actions Sheet (Long Press) */}
      {showQuickActions && (
        <>
          <div 
            className="fab-quick-actions-backdrop"
            onClick={() => setShowQuickActions(false)}
          />
          <div className="fab-quick-actions">
            <div className="quick-actions-header">
              <span>Quick Actions</span>
              <button 
                className="close-quick-actions"
                onClick={() => setShowQuickActions(false)}
              >
                ✕
              </button>
            </div>
            <div className="quick-actions-list">
              <button 
                className="quick-action"
                onClick={() => {
                  handleClick();
                  setShowQuickActions(false);
                }}
              >
                <span className="quick-action-icon">📷</span>
                <span className="quick-action-label">Take Photo</span>
              </button>
              <button 
                className="quick-action"
                onClick={() => {
                  window.history.back();
                  setShowQuickActions(false);
                }}
              >
                <span className="quick-action-icon">↩️</span>
                <span className="quick-action-label">Go Back</span>
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default FloatingActionButton;

